import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mail, Lock, ArrowRight, BarChart3, Tags, FileText, Shield, RefreshCw, ExternalLink } from 'lucide-react'
// Brand logo
import { authService } from '../services/auth'
import api from '../services/api'

// Google icon component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
)

interface LoginProps {
  onLogin: () => void
}

export default function Login({ onLogin }: LoginProps) {
  const googleCallbackPath = '/login/google/callback'
  const navigate = useNavigate()
  const [isRegister, setIsRegister] = useState(false)
  const [isForgotPassword, setIsForgotPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  
  // OTP state
  const [showOTP, setShowOTP] = useState(false)
  const [otpSession, setOtpSession] = useState('')
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', ''])
  const [resendCooldown, setResendCooldown] = useState(0)
  const [otpVerifying, setOtpVerifying] = useState(false) // Prevent double OTP verification
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([])
  
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: ''
  })
  
  // Google OAuth loading state
  const [googleLoading, setGoogleLoading] = useState(false)
  const [codeProcessed, setCodeProcessed] = useState(false)

  // Check for Google OAuth callback on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code') // Authorization code from Google
    const token = urlParams.get('token')
    const googleLogin = urlParams.get('google_login')
    const error = urlParams.get('error')
    
    // Helper function to complete login and redirect
    const completeLogin = () => {
      onLogin()
      // Reset URL to root to clear the OAuth callback path
      window.history.replaceState({}, '', '/')
      navigate('/', { replace: true })
    }
    
    console.log('[Login] useEffect triggered, code:', code ? code.substring(0, 20) + '...' : 'null', 'token:', token ? 'present' : 'null', 'codeProcessed:', codeProcessed)
    
    if (error) {
      setError(`Errore Google: ${error}`)
      // Clean URL completely
      window.history.replaceState({}, '', '/')
      return
    }
    
    // Handle authorization code from Google redirect - only process once
    if (code && !token && !codeProcessed) {
      console.log('[Login] Exchanging code for token...')
      setCodeProcessed(true) // Prevent double processing
      setGoogleLoading(true)
      
      // Clean URL immediately to prevent double processing
      window.history.replaceState({}, '', '/')
      
      const redirectUri = `${window.location.origin}${googleCallbackPath}`
      console.log('[Login] Using redirect_uri:', redirectUri)
      
      // Exchange code for token via our API
      api.post('/auth/google/callback', { 
        code,
        redirect_uri: redirectUri
      })
        .then(response => {
          console.log('[Login] API response:', response.data)
          if (response.data.access_token) {
            console.log('[Login] Token received, saving...')
            authService.setToken(response.data.access_token)
            completeLogin()
          } else if (response.data.requires_otp) {
            // OTP required
            console.log('[Login] OTP required for:', response.data.email)
            setOtpSession(response.data.otp_session)
            setForm(prev => ({ ...prev, email: response.data.email, name: response.data.name || '' }))
            setShowOTP(true)
            setGoogleLoading(false)
          } else {
            console.log('[Login] Unexpected response:', response.data)
            setError('Risposta inaspettata dal server')
            setGoogleLoading(false)
          }
        })
        .catch(err => {
          console.error('[Login] API error:', err.response?.data || err.message)
          setError(err.response?.data?.error || 'Errore durante il login con Google')
          setGoogleLoading(false)
        })
      return
    }
    
    // Handle direct token (legacy flow)
    if (token && googleLogin === 'success') {
      // Save token and redirect
      authService.setToken(token)
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname)
      completeLogin()
    }
  }, [onLogin, codeProcessed, navigate])

  // Handle Google login
  const handleGoogleLogin = async () => {
    setGoogleLoading(true)
    setError('')
    
    try {
      const response = await api.get('/auth/google', {
        params: {
          mode: isRegister ? 'register' : 'login',
          redirect_uri: `${window.location.origin}${googleCallbackPath}`
        }
      })
      
      // Redirect to Google OAuth
      window.location.href = response.data.url
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Errore durante la connessione a Google')
      setGoogleLoading(false)
    }
  }

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  // Auto-focus first OTP input when shown
  useEffect(() => {
    if (showOTP && otpInputRefs.current[0]) {
      otpInputRefs.current[0].focus()
    }
  }, [showOTP])

  const handleOTPChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return // Only digits
    
    const newOtp = [...otpCode]
    newOtp[index] = value.slice(-1) // Only last digit
    setOtpCode(newOtp)
    
    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus()
    }
  }

  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus()
    }
  }

  const handleOTPPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    const newOtp = [...otpCode]
    for (let i = 0; i < pasted.length; i++) {
      newOtp[i] = pasted[i]
    }
    setOtpCode(newOtp)
    if (pasted.length === 6) {
      otpInputRefs.current[5]?.focus()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setInfo('')

    try {
      if (isForgotPassword) {
        await api.post('/auth/forgot-password', { email: form.email })
        setInfo('Se questa email è registrata, riceverai un link per reimpostare la password.')
        setIsForgotPassword(false)
        setForm({ ...form, password: '' })
        return
      }

      let response
      if (isRegister) {
        response = await authService.register(form)
      } else {
        response = await authService.login({ email: form.email, password: form.password })
      }
      
      // Check if OTP is required
      if ('requires_otp' in response && response.requires_otp) {
        setOtpSession(response.otp_session)
        setShowOTP(true)
        setResendCooldown(60)
        setInfo(response.message)
      } else if ('access_token' in response) {
        // Direct login (legacy)
        onLogin()
        navigate('/', { replace: true })
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string; detail?: string } } }
      setError(error.response?.data?.error || error.response?.data?.detail || 'Errore durante l\'accesso')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOTP = async () => {
    // Prevent double submission
    if (otpVerifying) {
      console.log('[OTP] Already verifying, skipping duplicate call')
      return
    }
    
    const code = otpCode.join('')
    if (code.length !== 6) {
      setError('Inserisci il codice completo a 6 cifre')
      return
    }

    setOtpVerifying(true) // Mark as verifying
    setLoading(true)
    setError('')

    try {
      await authService.verifyOTP({
        otp_session: otpSession,
        otp_code: code
      })
      onLogin()
      navigate('/', { replace: true })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Codice non valido'
      setError(message)
      setOtpCode(['', '', '', '', '', ''])
      otpInputRefs.current[0]?.focus()
      setOtpVerifying(false) // Reset on error so user can retry
    } finally {
      setLoading(false)
    }
  }

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return
    
    setLoading(true)
    setError('')

    try {
      const response = await authService.resendOTP(form.email)
      if (response.otp_session) {
        setOtpSession(response.otp_session)
      }
      setInfo('Nuovo codice inviato!')
      setResendCooldown(60)
      setOtpCode(['', '', '', '', '', ''])
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nell\'invio del codice'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  // OTP Verification Screen
  if (showOTP) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="logo-sphere" style={{ width: '2rem', height: '2rem' }} />
            <span className="text-lg font-bold text-gray-900">PongoFin</span>
          </div>

          {/* OTP Card */}
          <div className="bg-white rounded-[20px] shadow-modal p-8">
            <div className="flex items-center justify-center mb-5">
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-primary-600" />
              </div>
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
              Verifica la tua identità
            </h2>
            <p className="text-gray-600 text-center text-sm mb-6">
              Abbiamo inviato un codice a 6 cifre a<br />
              <strong>{form.email}</strong>
            </p>

            {info && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm text-center">
                {info}
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm text-center">
                {error}
              </div>
            )}

            {/* OTP Input */}
            <div className="flex justify-center gap-2 mb-6" onPaste={handleOTPPaste}>
              {otpCode.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpInputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOTPChange(index, e.target.value)}
                  onKeyDown={(e) => handleOTPKeyDown(index, e)}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-[12px]
                    focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                    transition-all duration-200 ease-apple"
                />
              ))}
            </div>

            <button
              onClick={handleVerifyOTP}
              disabled={loading || otpCode.join('').length !== 6}
              className="w-full flex items-center justify-center gap-2 h-[44px] px-4
                bg-primary-600 text-white rounded-[12px] font-medium
                hover:bg-primary-700 hover:-translate-y-px hover:shadow-btn-hover
                active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30
                disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transition-all duration-200 ease-apple"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Verifica codice
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 mb-2">Non hai ricevuto il codice?</p>
              <button
                onClick={handleResendOTP}
                disabled={resendCooldown > 0 || loading}
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium
                  disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {resendCooldown > 0 ? `Riprova tra ${resendCooldown}s` : 'Invia nuovo codice'}
              </button>
            </div>

            <div className="mt-4 text-center">
              <button
                onClick={() => {
                  setShowOTP(false)
                  setOtpCode(['', '', '', '', '', ''])
                  setError('')
                  setInfo('')
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ← Torna al login
              </button>
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-gray-600">
            <a
              href="https://pongofin.com/privacy/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary-600 underline"
            >
              Privacy
            </a>
            <span className="mx-2 text-gray-400">|</span>
            <a
              href="https://pongofin.com/terms/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary-600 underline"
            >
              Termini di servizio
            </a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="logo-sphere" style={{ width: '2rem', height: '2rem' }} />
          <span className="text-lg font-bold text-gray-900">PongoFin</span>
        </div>
        <p className="text-gray-600 text-center text-sm mb-6">Financial Intelligence Platform</p>

        {/* Form Card */}
        <div className="bg-white rounded-[20px] shadow-modal p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {isForgotPassword ? 'Password dimenticata' : (isRegister ? 'Inizia con PongoFin' : 'Accedi')}
          </h2>

          {/* Registration starts from the pricing flow */}
          {isRegister && !isForgotPassword && (
            <div className="mb-6 p-4 bg-gradient-to-r from-primary-50 to-cyan-50 border border-primary-200 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-2">🚀 Per iniziare, scegli il tuo piano</h3>
              <p className="text-sm text-gray-600 mb-4">
                Ogni piano include 14 giorni gratis. Scegli il piano, completa il checkout e poi imposti la tua password.
              </p>
              <a
                href="https://pongofin.com#pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 w-full justify-center h-[44px] px-4
                  bg-primary-600 text-white rounded-[12px] font-medium
                  hover:bg-primary-700 hover:-translate-y-px hover:shadow-btn-hover
                  active:scale-[0.98] transition-all duration-200 ease-apple"
              >
                Visualizza i piani
                <ExternalLink className="w-4 h-4" />
              </a>
              <p className="text-xs text-gray-500 mt-3 text-center">
                Hai già un account? <button onClick={() => setIsRegister(false)} className="text-primary-600 hover:underline font-medium">Accedi qui</button>
              </p>
            </div>
          )}

          {info && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              {info}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Show login form only (not for register mode) */}
          {!isRegister && (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full pl-10 pr-4 border border-gray-300 rounded-[12px]
                        focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="nome@esempio.it"
                      required
                    />
                  </div>
                </div>

                {!isForgotPassword && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="password"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        className="w-full pl-10 pr-4 border border-gray-300 rounded-[12px]
                          focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="••••••••"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 h-[44px] px-4
                    bg-primary-600 text-white rounded-[12px] font-medium
                    hover:bg-primary-700 hover:-translate-y-px hover:shadow-btn-hover
                    active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transition-all duration-200 ease-apple"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      {isForgotPassword ? 'Invia link di reset' : 'Accedi'}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              {/* Google OAuth Button */}
              {!isForgotPassword && (
                <>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white text-gray-500">oppure</span>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={googleLoading || loading}
                    className="w-full flex items-center justify-center gap-3 h-[44px] px-4
                      bg-white text-gray-700 border border-gray-300 rounded-[12px] font-medium
                      hover:bg-gray-50 hover:-translate-y-px hover:shadow-elevated
                      active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300/50
                      disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transition-all duration-200 ease-apple"
                  >
                    {googleLoading ? (
                      <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <GoogleIcon />
                        Accedi con Google
                      </>
                    )}
                  </button>
                </>
              )}
            </>
          )}

          <div className="mt-6 text-center">
            {!isForgotPassword && (
              <button
                onClick={() => {
                  setIsRegister(!isRegister)
                  setError('')
                  setInfo('')
                }}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                {isRegister 
                  ? 'Hai già un account? Accedi' 
                  : 'Non hai un account? Registrati'}
              </button>
            )}

            <div className="mt-2">
              <button
                onClick={() => {
                  setIsRegister(false)
                  setIsForgotPassword(!isForgotPassword)
                  setError('')
                  setInfo('')
                }}
                className="text-sm text-gray-600 hover:text-gray-800 font-medium"
              >
                {isForgotPassword ? 'Torna al login' : 'Password dimenticata?'}
              </button>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-8 grid grid-cols-3 gap-4 text-center">
          <div className="p-3">
            <BarChart3 className="w-6 h-6 mx-auto mb-2 text-primary-600" />
            <p className="text-xs text-gray-600">Analisi automatica</p>
          </div>
          <div className="p-3">
            <Tags className="w-6 h-6 mx-auto mb-2 text-primary-600" />
            <p className="text-xs text-gray-600">Categorizzazione smart</p>
          </div>
          <div className="p-3">
            <FileText className="w-6 h-6 mx-auto mb-2 text-primary-600" />
            <p className="text-xs text-gray-600">Report dettagliati</p>
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-gray-600">
          <a
            href="https://pongofin.com/privacy/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary-600 underline"
          >
            Privacy
          </a>
          <span className="mx-2 text-gray-400">|</span>
          <a
            href="https://pongofin.com/terms/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary-600 underline"
          >
            Termini di servizio
          </a>
        </div>
      </div>
    </div>
  )
}
